import React, { Fragment, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import Accordion from '../../../../components/Accordion/Accordion'
import CheckBox from '../../../../components/CheckBox/CheckBox'
import clsx from 'clsx'
import { isNil } from 'lodash'
import { isEmpty } from 'ramda'
import Button from '../../../../components/Button/Button'
import Filter from '../../../../svg/Filter'
import Toggle from './Toggle'
import InputNumber from '../../../../components/InputNumber/InputNumber'
import ClickAwayListener from 'react-click-away-listener'

const defaultFilters = [
    {
        filterGrupName: 'Deals',
        checked: false,
        hasFilters: false,
    },
    {
        filterGrupName: 'Category',
        filters: [
            {
                filterName: 'All Classes',
                checked: true,
            },
            {
                filterName: 'Forklift',
                checked: false,
            },
            {
                filterName: 'Aerial Lift',
                checked: false,
            },
            {
                filterName: 'Railcar Mover',
                checked: false,
            },
            {
                filterName: 'Trailer Spotter',
                checked: false,
            },
        ],
    },
    {
        filterGrupName: 'Fuel Type',
        filters: [
            {
                filterName: 'All',
                checked: true,
            },
            {
                filterName: 'LPG',
                checked: false,
            },
            {
                filterName: 'Diesel',
                checked: false,
            },
            {
                filterName: 'Electric',
                checked: false,
            },
        ],
    },
    {
        filterGrupName: 'Capacity',
        valueMin: 3000,
        valueMax: 10000,
    },
]

const defaultCapacityFilter = defaultFilters.filter(
    filter => filter.filterGrupName === 'Capacity',
)[0]

const FilterTag = ({ text, onRemove }) => (
    <div className="w-auto h-auto py-1 px-2 max-w-full flex items-center justify-center bg-grayBackground text-black60">
        <XIcon
            role="button"
            className="w-4 mr-1 text-textDarkGray"
            onClick={onRemove}
        />
        <p className="truncate max-w-full">{text}</p>
    </div>
)

FilterTag.propTypes = {
    text: PropTypes.string,
    onRemove: PropTypes.func,
}

const UsedEquipmentFiltersModal = ({
    open,
    setOpen,
    filtersGroup,
    onFilter,
    // initialFilters,
    onClean,
}) => {
    const [allFilters, setAllFilters] = useState(
        filtersGroup?.map((filterG, index) => ({
            ...filterG,
            id: index,
            filters: filterG?.filters?.map((filter, indexFilter) => ({
                ...filter,
                id: indexFilter,
            })),
        })),
    )

    const [currentFilters, setCurrentFilters] = useState(allFilters)

    const hasDifferences =
        JSON.stringify(currentFilters) !== JSON.stringify(allFilters)

    const handleViewResults = () => {
        onFilter && onFilter(allFilters)
        setCurrentFilters(allFilters)
        setOpen(false)
    }

    const filtersGroupsWithActiveFilters = allFilters?.filter(filterG => {
        if (filterG?.hasFilters == false) {
            return filterG?.checked
        } else if (filterG?.filterGrupName === 'Capacity') {
            return (
                filterG?.valueMin != defaultCapacityFilter?.valueMin ||
                filterG?.valueMax != defaultCapacityFilter?.valueMax
            )
        } else {
            const filterGHasFilter = filterG?.filters?.find(
                f => f.checked === true,
            )
            return !isNil(filterGHasFilter)
        }
    })

    const onClearAllFilters = () => {
        onClean && onClean()
        const newFilters = defaultFilters?.map((filterG, index) => ({
            ...filterG,
            id: index,
            filters: filterG?.filters?.map((filter, indexFilter) => ({
                ...filter,
                id: indexFilter,
            })),
        }))
        setAllFilters(newFilters)
        setCurrentFilters(newFilters)
        onFilter(newFilters)
        setOpen(false)
    }

    const toggleFilterChecked = (filterGId, filterId) => {
        const newFilters = allFilters.map(filterG => {
            if (filterG?.id === filterGId) {
                return {
                    ...filterG,
                    filters: filterG?.filters?.map(filter => {
                        if (filter?.id === filterId) {
                            return {
                                ...filter,
                                checked: filter?.checked
                                    ? true
                                    : !filter?.checked,
                            }
                        } else {
                            return {
                                ...filter,
                                checked: false,
                            }
                        }
                    }),
                }
            }
            return filterG
        })
        setAllFilters(newFilters)
    }

    const setDefaultFilter = filterGId => {
        const newFilters = allFilters.map(filterG => {
            if (filterG?.id === filterGId) {
                return {
                    ...filterG,
                    filters: filterG?.filters?.map(filter => {
                        if (filter?.id === 0) {
                            return {
                                ...filter,
                                checked: true,
                            }
                        } else {
                            return {
                                ...filter,
                                checked: false,
                            }
                        }
                    }),
                }
            }
            return filterG
        })
        setAllFilters(newFilters)
    }

    const toggleFilterGWithoutFilters = (filterGId, newVal) => {
        const newFilters = allFilters.map(filterG => {
            if (filterG?.id === filterGId) {
                return {
                    ...filterG,
                    checked: newVal ? newVal : !filterG?.checked,
                }
            }
            return filterG
        })
        setAllFilters(newFilters)
    }

    const changeMinOrMaxCapacity = (filterGId, key, newVal) => {
        const newFilters = allFilters.map(filterG => {
            if (filterG?.id === filterGId) {
                return {
                    ...filterG,
                    [key]: newVal,
                }
            }
            return filterG
        })
        setAllFilters(newFilters)
    }

    const resetMinAndMaxFilters = filterGId => {
        const newFilters = allFilters.map(filterG => {
            if (filterG?.id === filterGId) {
                return {
                    ...filterG,
                    valueMin: defaultCapacityFilter?.valueMin,
                    valueMax: defaultCapacityFilter?.valueMax,
                }
            }
            return filterG
        })
        setAllFilters(newFilters)
    }

    const renderDeals = useCallback(() => {
        const dealsIsChecked = allFilters?.find(
            i => i?.filterGrupName == 'Deals',
        )
        return (
            <Toggle
                key="dealsContent"
                value={dealsIsChecked?.checked}
                onChange={newVal =>
                    toggleFilterGWithoutFilters(dealsIsChecked?.id, newVal)
                }
            />
        )
    }, [allFilters])

    const renderCapacity = useCallback(() => {
        const filterCapacity = allFilters?.find(
            i => i?.filterGrupName == 'Capacity',
        )
        return (
            <div
                key="capacityContent"
                className="w-full mt-4 h-auto flex flex-col items-start jsutify-start gap-y-2"
            >
                <InputNumber
                    onChange={val => {
                        changeMinOrMaxCapacity(
                            filterCapacity?.id,
                            'valueMin',
                            val,
                        )
                    }}
                    initialValue={filterCapacity?.valueMin}
                    max={filterCapacity?.valueMax}
                    min={3000}
                    label="Min. Capacity"
                    incrementNumber={500}
                    extraLabel="lbs"
                    className="w-full"
                />
                <InputNumber
                    onChange={val =>
                        changeMinOrMaxCapacity(
                            filterCapacity?.id,
                            'valueMax',
                            val,
                        )
                    }
                    initialValue={filterCapacity?.valueMax}
                    max={55000}
                    min={filterCapacity?.valueMin}
                    label="Max. Capacity"
                    incrementNumber={500}
                    extraLabel="lbs"
                    className="w-full"
                />
            </div>
        )
    }, [allFilters])

    const getSizeOfActiveFilters = () => {
        let filtersAppliedCount = 0
        filtersGroupsWithActiveFilters?.forEach(filterG => {
            if (filterG?.filters?.length != null) {
                filterG?.filters?.forEach(filter => {
                    if (
                        filter?.checked === true &&
                        filter?.filterName !== 'All Classes' &&
                        filter?.filterName !== 'All'
                    ) {
                        filtersAppliedCount++
                    }
                })
            } else {
                if (filterG?.checked === true) {
                    filtersAppliedCount++
                } else {
                    if (
                        filterG?.valueMin != defaultCapacityFilter?.valueMin ||
                        filterG?.valueMax != defaultCapacityFilter?.valueMax
                    ) {
                        filtersAppliedCount++
                    }
                }
            }
        })
        return filtersAppliedCount
    }

    const sizeOfActiveFilters = getSizeOfActiveFilters()

    return (
        <div className="flex flex-row items-center jusitfy-center md:w-auto w-full">
            <div className="relative w-full">
                <Button
                    variant="tertiary"
                    text="Filter By"
                    className="text-textDarkGray text-sm md:w-auto w-full"
                    icon={<Filter className="w-4 text-textDarkGray" />}
                    onClick={() => setOpen(true)}
                />
                {sizeOfActiveFilters > 0 && (
                    <span className="absolute -left-1 -top-1 w-5 text-xs h-5 text-white bg-textDarkerGray rounded-full flex items-center justify-center">
                        {sizeOfActiveFilters}
                    </span>
                )}
            </div>

            <Transition appear show={open} as={Fragment}>
                <div
                    className={clsx(
                        'absolute inset-0 w-full h-screen z-200 transition-all duration-300',
                        open ? 'bg-modalBackground' : 'bg-transparent',
                    )}
                >
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                        <Transition.Child
                            className="h-screen transition-all md:w-106 w-full border-l border-grayLines fixed right-0 top-0 bg-white z-170 duration-300 flex flex-col items-center justify-between"
                            enterFrom="opacity-30 -right-106"
                            enterTo="opacity-100 right-0"
                            leaveFrom="opacity-100 right-0"
                            leaveTo="opacity-0 -right-106"
                        >
                            <div className="flex flex-row items-center justify-between p-6 border-b border-grayLines w-full">
                                <p className="text text-black70 text-lg font-semibold">
                                    {'Filter By'}
                                </p>
                                <XIcon
                                    role="button"
                                    className="w-6 text-black70"
                                    onClick={() => setOpen(false)}
                                />
                            </div>
                            <div className="flex flex-col items-center justify-start w-full max-h-full overflow-auto flex-grow">
                                {!isEmpty(filtersGroupsWithActiveFilters) &&
                                    sizeOfActiveFilters > 0 && (
                                        <div className="flex flex-col items-center justify-start w-full m-auto p-6 border-b border-grayLines">
                                            <div className="w-full flex items-center justify-between">
                                                <p className="text-black font-semibold text-sm">
                                                    Applied Filters
                                                </p>
                                                <p
                                                    role="button"
                                                    onClick={() =>
                                                        onClearAllFilters()
                                                    }
                                                    className="text-links font-semibold text-sm hover:underline transition-all"
                                                >
                                                    Clear All
                                                </p>
                                            </div>

                                            <div className="w-full flex flex-row items-center justify-start gap-2 flex-wrap flex-grow mt-2">
                                                {filtersGroupsWithActiveFilters?.map(
                                                    filterG => {
                                                        if (
                                                            filterG?.filterGrupName ===
                                                            'Deals'
                                                        ) {
                                                            return (
                                                                <FilterTag
                                                                    key={`filterTag-deals`}
                                                                    text={
                                                                        filterG?.filterGrupName
                                                                    }
                                                                    onRemove={() =>
                                                                        toggleFilterGWithoutFilters(
                                                                            filterG?.id,
                                                                        )
                                                                    }
                                                                />
                                                            )
                                                        }

                                                        if (
                                                            filterG?.filterGrupName ===
                                                            'Capacity'
                                                        ) {
                                                            return (
                                                                <FilterTag
                                                                    key={`filterTag-capacity`}
                                                                    text={
                                                                        filterG?.filterGrupName
                                                                    }
                                                                    onRemove={() =>
                                                                        resetMinAndMaxFilters(
                                                                            filterG?.id,
                                                                        )
                                                                    }
                                                                />
                                                            )
                                                        }

                                                        return filterG?.filters?.map(
                                                            (filter, index) => {
                                                                return (
                                                                    filter?.checked &&
                                                                    filter?.filterName !==
                                                                        'All Classes' &&
                                                                    filter?.filterName !==
                                                                        'All' && (
                                                                        <FilterTag
                                                                            key={`filterTag-${index}`}
                                                                            text={
                                                                                filter?.filterName
                                                                            }
                                                                            onRemove={() =>
                                                                                setDefaultFilter(
                                                                                    filterG?.id,
                                                                                )
                                                                            }
                                                                        />
                                                                    )
                                                                )
                                                            },
                                                        )
                                                    },
                                                )}
                                            </div>
                                        </div>
                                    )}

                                <div className="flex flex-grow flex-col items-center justify-start w-full px-4">
                                    {allFilters.map((filterG, index) => {
                                        if (filterG?.hasFilters == false) {
                                            return renderDeals()
                                        }

                                        return (
                                            <Accordion
                                                hasBorder={
                                                    index !==
                                                    allFilters?.length - 1
                                                }
                                                key={`filterGroup-${filterG?.id}`}
                                                text={filterG.filterGrupName}
                                                content={
                                                    filterG?.filterGrupName ===
                                                    'Capacity' ? (
                                                        renderCapacity()
                                                    ) : (
                                                        <ul className="mt-4 flex flex-col items-start justify-start">
                                                            {filterG?.filters?.map(
                                                                (
                                                                    filter,
                                                                    indexF,
                                                                ) => {
                                                                    const filterIsChecked =
                                                                        filter.checked
                                                                    return (
                                                                        <li
                                                                            key={`filter-${filter?.id}`}
                                                                            className={clsx(
                                                                                indexF !==
                                                                                    filterG
                                                                                        ?.filters
                                                                                        ?.length -
                                                                                        1 &&
                                                                                    'mb-2',
                                                                            )}
                                                                        >
                                                                            <CheckBox
                                                                                isGroup
                                                                                defaultChecked={
                                                                                    filterIsChecked
                                                                                }
                                                                                onChange={() =>
                                                                                    toggleFilterChecked(
                                                                                        filterG?.id,
                                                                                        filter?.id,
                                                                                    )
                                                                                }
                                                                                label={
                                                                                    filter?.isLocation
                                                                                        ? filter?.customRender()
                                                                                        : filter?.filterName
                                                                                }
                                                                            />
                                                                        </li>
                                                                    )
                                                                },
                                                            )}
                                                        </ul>
                                                    )
                                                }
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                            <div className="p-6 w-full">
                                <Button
                                    variant="primary"
                                    text="View Results"
                                    className="w-full"
                                    disabled={!hasDifferences}
                                    onClick={() => handleViewResults()}
                                />
                            </div>
                        </Transition.Child>
                    </ClickAwayListener>
                </div>
            </Transition>
        </div>
    )
}

UsedEquipmentFiltersModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    filtersGroup: PropTypes.arrayOf(PropTypes.object),
    onFilter: PropTypes.func,
    initialFilters: PropTypes.object,
    onClean: PropTypes.func,
}

export default UsedEquipmentFiltersModal
